import React from 'react';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button, useDialogs } from '@michelin/acid-components';
import { Grid } from '@material-ui/core';
import { HoursOfOperation } from '../../Types';
import { GooglePlacesInfo } from '../../Types/GooglePlaces';
import { loadGooglePlacesInfo } from '../GooglePlaces';
import { HoursOfOperationView } from './View';
import { HoursOfOperationEdit } from './Edit';

interface HoursOfOperationsProps {
  googlePlacesId?: string | false,
  googlePlacesInfo?: GooglePlacesInfo,
  googlePlacesInfoOnChange?: (newGooglePlacesInfo: GooglePlacesInfo) => void,
  hoursOfOperation: HoursOfOperation,
  onChange: (newHoo: HoursOfOperation) => void,
  copyWorkHoursFrom?: () => void,
  country: string,
  readOnly: boolean,
  differentialUpdate?: boolean,
  copyMode?:boolean,
}

export default function HoursOfOperations(props: HoursOfOperationsProps) {
  const { t } = useTranslation();
  const { alertDialog } = useDialogs();
  let { googlePlacesInfo } = props;

  const copyWorkHoursFromGoogle = async () => {
    if (!googlePlacesInfo && props.googlePlacesId !== false) {
      const gpi = await loadGooglePlacesInfo(props.googlePlacesId || '');
      if (!gpi) {
        alertDialog(
          t('Please check your google places ID or try again later...').split('\n'),
          t('Error obtaining Hours of Operations hours form Google Places'),
          t('Ok'),
        );
        return;
      }
      googlePlacesInfo = gpi;
      if (props.googlePlacesInfoOnChange) props.googlePlacesInfoOnChange(gpi);
    }

    if (!googlePlacesInfo || !googlePlacesInfo.opening_hours) return;
    if (!props.onChange) return;

    const cloneHoursOfOperation = cloneDeep(props.hoursOfOperation);
    delete cloneHoursOfOperation.sunday;
    delete cloneHoursOfOperation.monday;
    delete cloneHoursOfOperation.tuesday;
    delete cloneHoursOfOperation.wednesday;
    delete cloneHoursOfOperation.thursday;
    delete cloneHoursOfOperation.friday;
    delete cloneHoursOfOperation.saturday;

    googlePlacesInfo.opening_hours.periods.forEach((period) => {
      if (!cloneHoursOfOperation) return;
      const dayWorkHours = {
        begin: `${period.open.time.substr(0, 2)}:${period.open.time.substr(2, 2)}`,
        end: `${period.close.time.substr(0, 2)}:${period.close.time.substr(2, 2)}`,
      };
      switch (period.open.day) {
        case 0: cloneHoursOfOperation.sunday = dayWorkHours; break;
        case 1: cloneHoursOfOperation.monday = dayWorkHours; break;
        case 2: cloneHoursOfOperation.tuesday = dayWorkHours; break;
        case 3: cloneHoursOfOperation.wednesday = dayWorkHours; break;
        case 4: cloneHoursOfOperation.thursday = dayWorkHours; break;
        case 5: cloneHoursOfOperation.friday = dayWorkHours; break;
        case 6: cloneHoursOfOperation.saturday = dayWorkHours; break;
        default: break;
      }
    });

    props.onChange(cloneHoursOfOperation);
  };

  return (
    <>
      {props.readOnly ? (
        <HoursOfOperationView
          holidays={props.country.toLowerCase()}
          hoursOfOperations={props.hoursOfOperation || {} as HoursOfOperation}
        />
      )
        : (
          <HoursOfOperationEdit
            holidays={props.country.toLowerCase()}
            hoursOfOperations={props.hoursOfOperation || {} as HoursOfOperation}
            onChange={(hoo: HoursOfOperation) => {
              if (!props.onChange) return;
              props.onChange(hoo);
            }}
            differentialUpdate={props.differentialUpdate || false}
            copyMode={props.copyMode}
          />
        )}
      {!props.readOnly ? (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            {props.copyWorkHoursFrom ? (
              <Button
                color="primary"
                variant="outlined"
                onClick={props.copyWorkHoursFrom}
                disabled={props.copyMode}
              >
                {t('Copy from Tire')}
              </Button>
            )
              : null}
          </Grid>
          {props.googlePlacesId !== false ? (
            <Grid item xs={12} md={6}>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => copyWorkHoursFromGoogle()}
                disabled={!props.googlePlacesId}
              >
                {t('Copy from Google')}
              </Button>
            </Grid>
          )
            : null}
        </Grid>
      )
        : null}
    </>
  );
}
